import { useCallback, useEffect, useRef, useState } from 'react';
import type { BuiltPlan, Category, OutingSummary, Person, Plan, Season, Suggestion } from '@ambit/shared';
// Constants come from the Zod-free subpath so the bundle stays lean.
import { CATEGORIES, DEFAULT_MAX_HOURS, SEASONS, currentSeason } from '@ambit/shared/constants';
import { ApiError, api } from './api.ts';
import { RouteCard } from './components/RouteCard.tsx';
import { SuggestBox, SuggestionList } from './components/Suggestions.tsx';
import { BeenThere, FeaturedDays } from './components/Memory.tsx';
import { Admin } from './components/Admin.tsx';
import { Household } from './components/Household.tsx';
import { Shortlist } from './components/Shortlist.tsx';
import { NeglectedRegions } from './components/NeglectedRegions.tsx';
import { OutingDetailDialog } from './components/OutingDetail.tsx';

type Shape = { season: Season | 'any'; maxDrive: number; budget: number };

const DRIVE_STEPS = [30, 45, 60, 90, 120, 180];
const BUDGET_STEPS = [0, 50, 100, 150, 250, 400];

/** How many hero cards to show before asking whether you really want them all. */
const HERO_PAGE = 24;

/**
 * Whether a code in the link has already been read on this page load.
 *
 * Module scope rather than component state because StrictMode mounts twice in
 * development, and the fragment can only be consumed once — the first pass
 * strips it from the address bar.
 */
let linkCodeConsumed = false;

export function App() {
  /**
   * Two separate questions that used to be one flag. `canRead` decides whether
   * the app renders at all; `hasSession` decides whether writing will work.
   * Collapsing them into a single `authed` meant that turning on public read
   * made the app believe everyone was signed in — so it offered no way to sign
   * in, and every write failed with a raw 401 and no route out of it.
   */
  const [canRead, setCanRead] = useState<boolean | null>(null);
  const [hasSession, setHasSession] = useState(false);
  /**
   * Only an identity session can administer households. A household session
   * gets a 404 from every /admin route, so this decides whether to offer the
   * link at all rather than whether the request would succeed.
   */
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [signInReason, setSignInReason] = useState<SignInReason>('anonymous');
  /** How many this household plans for. Null when signed out or unstated. */
  const [partySize, setPartySize] = useState<number | null>(null);
  const [oidcAvailable, setOidcAvailable] = useState(false);
  // Season defaults to the one we are in, not 'any' — the question this screen
  // answers is "what should we do this weekend", and 'any' let a July visit
  // surface a December illumination. Still switchable, including back to 'any'.
  const [shape, setShape] = useState<Shape>({
    season: currentSeason(),
    maxDrive: 90,
    budget: 250,
  });
  const [category, setCategory] = useState<Category | 'all'>('all');
  const [regionFilter, setRegionFilter] = useState<string | null>(null);
  const [heroes, setHeroes] = useState<OutingSummary[]>([]);
  const [heroTotal, setHeroTotal] = useState(0);
  const [loadingHeroes, setLoadingHeroes] = useState(true);
  const [showAllHeroes, setShowAllHeroes] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [plan, setPlan] = useState<BuiltPlan | null>(null);
  const [busySlot, setBusySlot] = useState<'food' | 'addon' | null>(null);
  const [saving, setSaving] = useState(false);
  const [people, setPeople] = useState<Person[]>([]);
  const [me, setMe] = useState<string | null>(localStorage.getItem('ambit.me'));
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [savedTick, setSavedTick] = useState(0);
  const [needsIdentity, setNeedsIdentity] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Sign in from a link, so nobody types a code on a phone keyboard.
   *
   *     https://ambit.holthome.net/#code=otter-canoe-maple
   *
   * In the FRAGMENT rather than the query string, deliberately. Query strings
   * are logged in full — the service journal already contains lines like
   * `GET /api/auth/callback?code=...` — so `?code=` would write the family code
   * into journald on every click and leave it there for the retention period.
   * A fragment never leaves the browser.
   *
   * Path-agnostic on purpose: this reads the hash wherever it is, so a shared
   * plan link can carry one too — `/plan/<uuid>#code=...` will work the day
   * that route exists, with no change here.
   *
   * Goes through the same POST /api/auth as typing it, so the rate limit and
   * the hashing apply identically. A second way in is how a bypass appears.
   */
  const consumeLinkCode = useCallback(async () => {
    const match = /(?:^|&)code=([^&]+)/.exec(window.location.hash.replace(/^#/, ''));
    if (!match) return;

    // Clear it first. If the sign-in throws, the code should still be gone from
    // the address bar rather than sitting there to be screenshotted.
    history.replaceState(null, '', window.location.pathname + window.location.search);

    try {
      await api.login(decodeURIComponent(match[1]!));
      setHasSession(true);
      setCanRead(true);
      void api.listPeople().then(setPeople).catch(() => undefined);
      void api.listSuggestions().then(setSuggestions).catch(() => undefined);
    } catch {
      // Almost always a link written before the code was rotated. Say so,
      // rather than rendering a signed-out app with no explanation for why
      // voting quietly does nothing.
      setSignInReason('stale-link');
      setSignInOpen(true);
    }
  }, []);

  useEffect(() => {
    async function run() {
      /*
       * Guarded at module scope rather than with a cancellation flag. React
       * StrictMode runs this twice in development: the first pass consumed the
       * hash and started the sign-in, the cleanup marked it cancelled, and the
       * failure message was then swallowed by its own guard. Production was
       * fine and development silently wasn't, which is the worse way round.
       */
      if (!linkCodeConsumed) {
        linkCodeConsumed = true;
        await consumeLinkCode();
      }
      const status = await api.authStatus();
      setCanRead(status.authenticated || status.publicRead);
      setHasSession(status.authenticated);
      setOidcAvailable(status.oidcAvailable);
      setPartySize(status.partySize);
    }
    void run();
  }, [consumeLinkCode]);

  /*
   * Tapping a link to a page you are already on changes the fragment without
   * reloading, so no mount effect fires and the code is silently ignored.
   * Found by doing exactly that while testing.
   */
  useEffect(() => {
    const onHashChange = () => void consumeLinkCode();
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, [consumeLinkCode]);

  /**
   * A 401 is not an error to report, it is a missing session — so it opens the
   * way to fix it instead of printing the server's sentence. That sentence is
   * written for whoever is holding curl ("POST /api/auth with { passphrase }"),
   * which is exactly the wrong audience for a phone in a kitchen.
   *
   * `session_superseded` is the other kind of 401: you were signed in, and the
   * household code was changed underneath you. Offering the same "browsing is
   * open to everyone" copy would be a small lie, and would leave someone
   * retyping a code that stopped working an hour ago.
   */
  const reportError = useCallback((e: unknown) => {
    if (e instanceof ApiError && e.status === 401) {
      setError(null);
      setSignInReason(e.code === 'session_superseded' ? 'superseded' : 'anonymous');
      if (e.code === 'session_superseded') setHasSession(false);
      setSignInOpen(true);
      return;
    }
    setError(describe(e));
  }, []);

  useEffect(() => {
    if (!canRead) return;
    setLoadingHeroes(true);
    void api
      .listOutings({
        category: category === 'all' ? undefined : category,
        region: regionFilter ?? undefined,
        season: shape.season,
        maxDrive: shape.maxDrive,
        /*
         * The budget is for the whole day, but a hero costing more than all of
         * it cannot produce one — so it belongs out of the list rather than in
         * it producing a plan that opens with an apology.
         *
         * Not stricter than that on purpose: a hero at exactly the budget is
         * still a day, because a picnic is free. Two of these three controls
         * used to filter and this one quietly did not.
         */
        maxCost: shape.budget,
        // Lead with the things that fill a day. Sorting by proximity puts the
        // antiques district above Old Rag, which makes the catalog look dull.
        sort: 'substance',
        // Fetch the whole match set: "Surprise us" should be able to land on
        // anything that matches, not just the first page of it.
        limit: 200,
      })
      .then((r) => {
        setHeroes(r.data);
        setHeroTotal(r.meta?.total ?? r.data.length);
        setShowAllHeroes(false);
      })
      .catch((e: unknown) => reportError(e))
      .finally(() => setLoadingHeroes(false));
    void api.listPeople().then(setPeople).catch(() => undefined);
  }, [canRead, category, regionFilter, shape.season, shape.maxDrive, shape.budget, reportError]);

  useEffect(() => {
    if (!canRead) return;
    void api.listSuggestions().then(setSuggestions).catch(() => undefined);
  }, [canRead]);

  const build = useCallback(
    async (heroId: string, pinnedId?: string) => {
      setError(null);
      try {
        setPlan(await api.buildPlan(heroId, pinnedId ? { ...shape, pinnedIds: [pinnedId] } : shape));
        queueMicrotask(() => document.getElementById('route-heading')?.scrollIntoView({ behavior: 'smooth' }));
      } catch (e: unknown) {
        reportError(e);
      }
    },
    [shape],
  );

  const reroll = useCallback(
    async (slot: 'food' | 'addon', currentId: string) => {
      if (!plan) return;
      setBusySlot(slot);
      try {
        setPlan(
          await api.rerollSlot({
            heroId: plan.heroId,
            slot,
            currentId,
            // Identify what to keep by the stop being replaced, not by its
            // slot. A food-led day has two add-ons, so filtering by slot threw
            // away the sibling activity every time you rerolled one of them.
            keepIds: plan.stops.filter((s) => s.outing.id !== currentId).map((s) => s.outing.id),
            constraints: shape,
          }),
        );
      } catch (e: unknown) {
        reportError(e);
      } finally {
        setBusySlot(null);
      }
    },
    [plan, shape],
  );

  const save = useCallback(async () => {
    if (!plan) return;
    setSaving(true);
    try {
      await api.savePlan(plan, me);
      setSavedTick((n) => n + 1);
      setError(null);
    } catch (e: unknown) {
      reportError(e);
    } finally {
      setSaving(false);
    }
  }, [plan, me]);

  const openSaved = useCallback(
    (planId: string) => {
      void api
        .getPlan(planId)
        .then((saved) => {
          setPlan(planToBuilt(saved, shape.budget));
          document.getElementById('route-heading')?.scrollIntoView({ behavior: 'smooth' });
        })
        .catch((e: unknown) => reportError(e));
    },
    [shape.budget],
  );

  if (canRead === null) return <Splash>Loading…</Splash>;
  if (!canRead)
    return (
      <Gate
        onDone={() => {
          setCanRead(true);
          setHasSession(true);
        }}
        oidcAvailable={oidcAvailable}
      />
    );

  // 147 cards is a database browser, not a decision aid. Show a first screen of
  // the most substantial matches and keep the rest one tap away.
  const visibleHeroes = showAllHeroes ? heroes : heroes.slice(0, HERO_PAGE);

  return (
    <main className="mx-auto max-w-2xl px-5 pb-24 pt-10 lg:max-w-4xl">
      <header className="border-b border-spruce-800 pb-6">
        {/*
          Auth sits beside the wordmark rather than under the tagline. It used
          to be the first interactive element on the page — a filled button
          between "pick the one thing you want to do" and step 01, reading as a
          step zero of a flow it is not part of, and implying a gate that does
          not exist. The whole catalog browses without a session; only voting,
          logging and suggesting need one, and each of those asks at the moment
          it is needed.

          Kept as a link rather than removed: without it there is no way in at
          all until a write fails, which is the state this replaced.
        */}
        <div className="flex items-start justify-between gap-6">
          <div className="min-w-0">
            <Wordmark />
            <p className="mt-4 max-w-lg text-pretty text-base text-bone-300">
              Pick the one thing you want to do. We&rsquo;ll build the day around it.
            </p>
          </div>

          <nav aria-label="Account" className="flex shrink-0 flex-col items-end gap-2 pt-2">
            {!hasSession ? (
              <button
                type="button"
                onClick={() => setSignInOpen(true)}
                className="text-sm text-bone-500 underline-offset-4 transition-colors hover:text-bone-300 hover:underline"
              >
                Sign in
              </button>
            ) : (
              /*
                Quiet on purpose: signing out is rare and should not compete
                with deciding on a Saturday. But it has to exist — without it
                you cannot hand the phone to another household, and you cannot
                leave a session behind on a borrowed device.
              */
              <button
                type="button"
                onClick={() => {
                  void api
                    .logout()
                    .catch(() => undefined)
                    .finally(() => {
                      setHasSession(false);
                      setIsAdmin(false);
                      setShowAdmin(false);
                      // Everything below this line was fetched with the session.
                      // Leaving it on screen would show one household's roster to
                      // whoever signs in next.
                      setPeople([]);
                      setSuggestions([]);
                    });
                }}
                className="text-sm text-bone-500 underline-offset-4 transition-colors hover:text-bone-300 hover:underline"
              >
                Sign out
              </button>
            )}
            {isAdmin && (
              <button
                type="button"
                onClick={() => setShowAdmin((v) => !v)}
                className="text-sm text-bone-500 underline-offset-4 transition-colors hover:text-bone-300 hover:underline"
              >
                {showAdmin ? 'Hide households' : 'Households'}
              </button>
            )}
          </nav>
        </div>
      </header>

      {error && (
        <p role="alert" className="mt-6 border-l-2 border-blaze-500 bg-spruce-900 px-4 py-3 text-sm text-blaze-300">
          {error}
        </p>
      )}

      {/* ── 1. shape the day ─────────────────────────────────────────── */}
      <section aria-labelledby="shape-heading" className="mt-8">
        <Step n={1} id="shape-heading">
          Shape the day
        </Step>
        <div className="mt-4 space-y-4">
          <Choices
            label="Season"
            options={['any', ...SEASONS]}
            value={shape.season}
            onChange={(v) => setShape((s) => ({ ...s, season: v as Season | 'any' }))}
          />
          <Choices
            label="Max drive"
            options={DRIVE_STEPS}
            value={shape.maxDrive}
            format={(v) => `${v}m`}
            onChange={(v) => setShape((s) => ({ ...s, maxDrive: Number(v) }))}
          />
          <Choices
            label="Budget for five"
            options={BUDGET_STEPS}
            value={shape.budget}
            format={(v) => (v === 0 ? 'free' : `$${v}`)}
            onChange={(v) => setShape((s) => ({ ...s, budget: Number(v) }))}
          />
        </div>
      </section>

      {/* ── 2. pick a hero ───────────────────────────────────────────── */}
      <section aria-labelledby="hero-heading" className="mt-10">
        <Step n={2} id="hero-heading">
          Pick the reason
        </Step>
        <div className="mt-4 flex items-center justify-between gap-3">
          <Choices
            label="Kind"
            options={['all', ...CATEGORIES]}
            value={category}
            onChange={(v) => setCategory(v as Category | 'all')}
          />
        </div>

        <NeglectedRegions
          activeRegion={regionFilter}
          onPick={(region) => {
            setRegionFilter(region.regionId);
            // Only open up the drive limit if the region is genuinely out of
            // reach. Silently moving someone's setting to visit somewhere
            // twenty minutes away is presumptuous.
            const needed = region.nearestDriveMinutes ?? 0;
            if (needed > shape.maxDrive) {
              setShape((s) => ({ ...s, maxDrive: DRIVE_STEPS.find((d) => d >= needed) ?? needed }));
            }
          }}
          onClear={() => setRegionFilter(null)}
        />

        <div className="mt-3 flex items-baseline justify-between">
          <span className="numeric text-xs text-bone-500" aria-live="polite">
            {loadingHeroes ? 'looking…' : `${heroTotal} match`}
          </span>
          <button
            type="button"
            disabled={heroes.length === 0}
            onClick={() => void build(heroes[Math.floor(Math.random() * heroes.length)]!.id)}
            className="text-sm text-blaze-400 underline decoration-dotted underline-offset-4 hover:text-blaze-300 disabled:opacity-40"
          >
            Surprise us
          </button>
        </div>

        <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {visibleHeroes.map((outing) => (
            /*
              Two actions, so two buttons — siblings, never nested. A <button>
              inside a <button> is invalid HTML and browsers resolve it by
              dropping one, so the "more" affordance has to sit alongside the
              card rather than inside it. The card keeps the whole-surface hit
              target it had; only the title yields the corner it needs.
            */
            <li key={outing.id} className="relative">
              <button
                type="button"
                onClick={() => void build(outing.id)}
                aria-current={plan?.heroId === outing.id}
                className={`flex h-full w-full flex-col rounded-md p-3.5 text-left transition-colors ${
                  plan?.heroId === outing.id
                    ? 'bg-spruce-700'
                    : 'bg-spruce-800 hover:bg-spruce-700'
                }`}
              >
                {/*
                  No border: the surface step carries the grouping and the
                  selected state is carried by colour on the title, which is a
                  4.8:1 change rather than a 1.2:1 one.
                */}
                <div
                  className={`display pr-12 text-lg font-semibold leading-tight ${
                    plan?.heroId === outing.id ? 'text-blaze-400' : 'text-bone-100'
                  }`}
                >
                  {outing.name}
                </div>
                <div className="text-xs text-bone-500">{outing.locality}</div>
                {/*
                  Shown rather than filtered out. A festival opening in six
                  weeks is exactly the thing worth earmarking, and hiding it
                  until it starts means meeting it the week it is too late to
                  plan around. The accent is the same one `free` gets: this is
                  a fact that changes what you do with the card.
                */}
                {startsLater(outing.nextOccurrence) && (
                  <div className="numeric mt-1 text-xs text-blaze-400">
                    from {formatRunDate(outing.nextOccurrence!.startsOn)}
                  </div>
                )}
                {/*
                  The catalog's whole value is this sentence. Two lines is the
                  most a 3-up grid can carry without the cards turning into
                  paragraphs you skim past, and every blurb in the seed leads
                  with its distinguishing detail, so a clamp truncates the
                  qualifier rather than the point.
                */}
                <p className="mt-2 line-clamp-2 text-xs leading-snug text-bone-300">
                  {outing.blurb}
                </p>
                <div className="numeric mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-bone-500">
                  {/* Free is the thing people scan for, so it gets the accent. */}
                  <span className={outing.costUsd === 0 ? 'text-blaze-400' : 'text-bone-300'}>
                    {outing.costUsd === 0 ? 'free' : `$${outing.costUsd}`}
                  </span>
                  {/*
                    Two numbers, both in minutes-and-hours, sitting side by side
                    and meaning opposite things: one is the day, the other is the
                    tax you pay to get to it. "4h 40m" read as a single duration
                    until you knew the convention. The glyph does the work a word
                    would, in the space the 3-up grid actually has — "4h there
                    40m drive" wraps to a second line on most cards.

                    The words are still there for screen readers, which had the
                    same ambiguity and no glyph to resolve it.
                  */}
                  <span className="inline-flex items-center gap-1" title="Time on the ground">
                    <ClockGlyph />
                    <span className="sr-only">{' time there '}</span>
                    {outing.hours}h
                  </span>
                  <span className="inline-flex items-center gap-1" title="Drive from home, one way">
                    <CarGlyph />
                    <span className="sr-only">{' drive '}</span>
                    {outing.driveMinutes}m
                  </span>
                  {/*
                    Effort only exists for the 31 outings where it's a real
                    question. Rendering "moderate" on a creamery would be noise,
                    and `strenuous` is the one value that should stop someone,
                    so it's the only one that takes a colour.
                  */}
                  {outing.effort && (
                    <span
                      className={
                        outing.effort === 'strenuous' ? 'text-blaze-300' : 'text-bone-500'
                      }
                    >
                      {` ${outing.effort}`}
                    </span>
                  )}
                  {/*
                    Deliberately muted. Booking is the normal condition for good
                    things — 17 of the top 24 need it — and an accent that lands
                    on 71% of cards has stopped being an accent. Orange is kept
                    for the two genuine outliers, `free` and `strenuous`, which
                    would otherwise have to compete with it for attention.
                  */}
                  {/*
                    Leading spaces here and on `effort` for the same reason the
                    wordmark carries them: flex `gap` separates these visually
                    but not textually, so the button's accessible name was one
                    run of "30mbook ahead". The browser trims the space at the
                    start of each flex item's line box, so nothing moves.
                  */}
                  {outing.bookingNote && <span>{' book ahead'}</span>}
                </div>
              </button>
              {/*
                Named for its subject, not its shape. Forty-two buttons all
                announcing "more" is a screen-reader listing of forty-two
                identical rows; the visible word can stay short because the
                accessible name carries the outing.
              */}
              <button
                type="button"
                onClick={() => setDetailId(outing.id)}
                aria-label={`More about ${outing.name}`}
                className="absolute right-2 top-2 rounded px-1.5 py-0.5 text-xs text-bone-500 transition-colors hover:bg-spruce-600 hover:text-bone-100"
              >
                more
              </button>
            </li>
          ))}
        </ul>
        {heroes.length > visibleHeroes.length && (
          <button
            type="button"
            onClick={() => setShowAllHeroes(true)}
            className="mt-3 w-full rounded-md bg-spruce-800 px-4 py-2.5 text-sm text-bone-300 transition-colors hover:bg-spruce-700 hover:text-bone-100"
          >
            Show the other {heroes.length - visibleHeroes.length}
          </button>
        )}
        {heroes.length === 0 && !loadingHeroes && (
          <p className="mt-4 text-sm text-bone-500">
            Nothing matches. Try a longer drive or a different season.
          </p>
        )}
      </section>

      {/* ── 3. the route ─────────────────────────────────────────────── */}
      {plan && (
        <RouteCard plan={plan} busySlot={busySlot} onReroll={reroll} onSave={() => void save()} saving={saving} />
      )}

      {/* ── 4. the shortlist ─────────────────────────────────────────── */}
      <Shortlist
        people={people}
        me={me}
        refreshKey={savedTick}
        onOpen={openSaved}
        onNeedIdentity={() => {
          setNeedsIdentity(true);
          document.getElementById('who-heading')?.scrollIntoView({ behavior: 'smooth' });
        }}
      />

      {/* ── featured days ────────────────────────────────────────────── */}
      <FeaturedDays
        onOpen={(saved) => {
          setPlan(planToBuilt(saved, shape.budget));
          queueMicrotask(() =>
            document.getElementById('route-heading')?.scrollIntoView({ behavior: 'smooth' }),
          );
        }}
      />

      {/* ── suggestions ──────────────────────────────────────────────── */}
      <section aria-labelledby="ideas-heading" className="mt-12 border-t border-spruce-800 pt-6">
        <h2 id="ideas-heading" className="display text-xs uppercase tracking-widest text-bone-500">
          Ideas to look into
        </h2>
        <p className="mt-1 text-sm text-bone-500">
          Anything you want to do. We&rsquo;ll work out the price and the hours.
        </p>
        <SuggestBox
          people={people}
          me={me}
          onAdded={(created) => setSuggestions((all) => [created, ...all])}
        />
        <SuggestionList
          suggestions={suggestions}
          onDeclined={(updated) =>
            setSuggestions((all) => all.map((s) => (s.id === updated.id ? updated : s)))
          }
        />
      </section>

      {/* ── been there ───────────────────────────────────────────────── */}
      <BeenThere
        people={people}
        me={me}
        hasSession={hasSession}
        onPersonAdded={(person) => setPeople((all) => [...all, person])}
        onNeedsSignIn={() => {
          setSignInReason('anonymous');
          setSignInOpen(true);
        }}
      />

      {hasSession && (
        <Household
          people={people}
          me={me}
          needsIdentity={needsIdentity}
          onPick={(id) => {
            setMe(id);
            localStorage.setItem('ambit.me', id);
            setNeedsIdentity(false);
          }}
          onAdded={(person) => setPeople((all) => [...all, person])}
        />
      )}

      <OutingDetailDialog
        id={detailId}
        partySize={partySize}
        onClose={() => setDetailId(null)}
        onBuild={(heroId, pinnedId) => {
          setDetailId(null);
          void build(heroId, pinnedId);
        }}
      />

      <SignInDialog
        open={signInOpen}
        reason={signInReason}
        onClose={() => setSignInOpen(false)}
        oidcAvailable={oidcAvailable}
        onDone={() => {
          setHasSession(true);
          setSignInOpen(false);
          setError(null);
          // Everything gated behind a session was skipped on load; pull it now
          // rather than making someone reload to see the people roster appear.
          void api.listPeople().then(setPeople).catch(() => undefined);
          void api.listSuggestions().then(setSuggestions).catch(() => undefined);
        }}
      />
    </main>
  );
}

/**
 * The wordmark and its gloss.
 *
 * The name is the product thesis in one word — an ambit is the bounded range
 * within which something moves, which is exactly what this is: a map of what is
 * reachable from one house in an afternoon. Nobody knows the word cold, so it
 * gets a dictionary entry rather than being left to look like a typo.
 *
 * A condensed face earns the right to be set this large: at 700 weight it still
 * occupies less width than the body font would at half the scale. That is the
 * whole reason to use one.
 */
function Wordmark() {
  return (
    <>
      <h1 className="display text-6xl font-bold uppercase leading-[0.85] tracking-[-0.02em] text-bone-100 sm:text-7xl">
        Ambit
      </h1>
      {/* Tight to the wordmark (mt-2.5) so it reads as one lockup, not a third
          stacked paragraph. The separator carries literal spaces rather than
          relying on margin: with margin alone the text content is "nounthe
          range…", which is what a screen reader announces. */}
      <p className="mt-2.5 max-w-md text-sm leading-snug text-bone-500">
        <span className="numeric text-[11px] uppercase tracking-widest text-blaze-400">noun</span>
        <span className="text-spruce-600">{' · '}</span>
        the range or extent within which something moves — from the Latin{' '}
        <i lang="la" className="text-bone-300">
          ambitus
        </i>
        , &ldquo;a going around.&rdquo;
      </p>
    </>
  );
}

/**
 * Dated entries whose next run has not begun. The comparison is on ISO date
 * strings, which sort correctly as text, so no Date parsing and no timezone to
 * get wrong for something this coarse.
 */
function startsLater(next: { startsOn: string } | null | undefined): boolean {
  return next !== null && next !== undefined && next.startsOn > new Date().toISOString().slice(0, 10);
}

/** "15 Aug", or "15 Aug 2027" when it is not this year. */
function formatRunDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y!, m! - 1, d!);
  const thisYear = y === new Date().getFullYear();
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    ...(thisYear ? {} : { year: 'numeric' }),
  });
}

function Step({ n, id, children }: { n: number; id: string; children: React.ReactNode }) {
  return (
    <h2 id={id} className="flex items-baseline gap-3">
      <span className="numeric text-sm text-blaze-400">{String(n).padStart(2, '0')}</span>
      <span className="display text-3xl font-semibold uppercase tracking-tight text-bone-100 sm:text-4xl">
        {children}
      </span>
    </h2>
  );
}

/**
 * The only two icons in the app, and they earn it: they separate a pair of
 * numbers that are otherwise identical in form and opposite in meaning.
 *
 * Drawn rather than installed. An icon package would be several hundred
 * kilobytes and a build-time dependency for twelve pixels of line work, and
 * emoji were the other option — they arrive full-colour and platform-shaped,
 * which is a poor fit for a palette this restrained.
 *
 * Both are stroked in `currentColor` so they inherit whatever the row is
 * doing, and both are `aria-hidden`: the meaning lives in the `sr-only` words
 * beside them, not here.
 */
function ClockGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3 w-3 shrink-0" fill="none">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.4" />
      <path
        d="M8 4.4V8.2l2.4 1.5"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CarGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" className="h-3 w-3 shrink-0" fill="none">
      {/* Roof and bonnet in one stroke, wheels sitting on the same baseline.
          Anything more detailed turns to mush at twelve pixels — the wheels are
          filled rather than outlined because a 1.3 radius with a 1.4 stroke
          leaves no hole and renders as a smudge. */}
      <path
        d="M2.4 10.6V8.6l1.5-3.1a1.3 1.3 0 0 1 1.17-.74h5.86a1.3 1.3 0 0 1 1.17.74l1.5 3.1v2"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M2.4 9.1h11.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <circle cx="5.4" cy="11.6" r="1.15" fill="currentColor" />
      <circle cx="10.6" cy="11.6" r="1.15" fill="currentColor" />
    </svg>
  );
}

function Choices<T extends string | number>({
  label,
  options,
  value,
  onChange,
  format,
}: {
  label: string;
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
  format?: (value: T) => string;
}) {
  return (
    <fieldset>
      <legend className="display text-[10px] uppercase tracking-widest text-bone-500">{label}</legend>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {options.map((option) => (
          <button
            key={String(option)}
            type="button"
            aria-pressed={option === value}
            onClick={() => onChange(option)}
            className={`rounded-md px-3 py-1.5 text-sm capitalize transition-colors ${
              option === value
                ? 'bg-blaze-500 font-semibold text-spruce-950'
                : 'bg-spruce-800 text-bone-300 hover:bg-spruce-700 hover:text-bone-100'
            }`}
          >
            {format ? format(option) : String(option)}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * The full-page front door, shown when public read is off and there is nothing
 * to look at without signing in.
 */
function Gate({ onDone, oidcAvailable }: { onDone: () => void; oidcAvailable: boolean }) {
  // Surfaced by the PocketID callback when a sign-in was refused.
  const authError = new URLSearchParams(window.location.search).get('auth_error');

  return (
    <Splash>
      <div className="w-full max-w-sm">
        <Wordmark />
        {authError && (
          <p role="alert" className="mt-4 text-sm text-blaze-400">
            Sign-in was not completed ({authError}).
          </p>
        )}
        <SignInForm onDone={onDone} oidcAvailable={oidcAvailable} className="mt-6" />
      </div>
    </Splash>
  );
}

/**
 * The same two doors, reusable. Extracted from `Gate` so the dialog that opens
 * on a 401 offers exactly the sign-in the front door does — one implementation,
 * so the passphrase field and the PocketID button cannot drift apart.
 */
function SignInForm({
  onDone,
  oidcAvailable,
  className = '',
}: {
  onDone: () => void;
  oidcAvailable: boolean;
  className?: string;
}) {
  const [passphrase, setPassphrase] = useState('');
  const [error, setError] = useState<string | null>(null);

  return (
    <div className={className}>
      {/*
        Two ways in, on purpose. The kids use the shared passphrase — nobody
        is provisioning a teenager an IdP account to vote on a hike — while a
        PocketID login gives a real identity for anyone who has one.
      */}
      {oidcAvailable && (
        <>
          <a
            href={`/api/auth/login?return_to=${encodeURIComponent(window.location.pathname)}`}
            className="block w-full rounded-md bg-blaze-500 px-4 py-2.5 text-center text-sm font-semibold text-spruce-950 transition-colors hover:bg-blaze-400"
          >
            Sign in with PocketID
          </a>
          <div className="mt-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-spruce-800" />
            <span className="display text-[10px] uppercase tracking-widest text-bone-500">or</span>
            <span className="h-px flex-1 bg-spruce-800" />
          </div>
        </>
      )}

      <form
        className="mt-5"
        onSubmit={(event) => {
          event.preventDefault();
          api
            .login(passphrase)
            .then(onDone)
            .catch(() => setError('That passphrase is not right.'));
        }}
      >
        <label htmlFor="passphrase" className="block text-sm text-bone-500">
          Household passphrase
        </label>
        <input
          id="passphrase"
          type="password"
          value={passphrase}
          autoComplete="current-password"
          onChange={(e) => setPassphrase(e.target.value)}
          className="mt-1.5 w-full rounded-md bg-spruce-800 px-3 py-2.5 text-bone-100"
        />
        {error && (
          <p role="alert" className="mt-2 text-sm text-blaze-400">
            {error}
          </p>
        )}
        <button
          type="submit"
          className={`mt-4 w-full rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
            oidcAvailable
              ? 'bg-spruce-700 text-bone-100 hover:bg-spruce-600'
              : 'bg-blaze-500 text-spruce-950 hover:bg-blaze-400'
          }`}
        >
          Let me in
        </button>
      </form>
    </div>
  );
}

/**
 * Why the sign-in dialog opened.
 *
 * `anonymous`  — browsing on public read, tried to do something that is the
 *                household's. Never signed in.
 * `superseded` — was signed in, and the household code changed. Usually
 *                because a phone went missing and somebody rotated it.
 * `stale-link` — followed a link carrying a code that no longer works, which
 *                means the same thing but from the other direction: the link
 *                was written before the rotation.
 */
type SignInReason = 'anonymous' | 'superseded' | 'stale-link';

/**
 * Opened when a write comes back 401 — that is, when someone browsing on public
 * read tries to vote, log a day or suggest something. It explains what happened
 * in a sentence about the day rather than about HTTP, and puts the fix directly
 * under it.
 */
function SignInDialog({
  open,
  reason,
  onClose,
  onDone,
  oidcAvailable,
}: {
  open: boolean;
  reason: SignInReason;
  onClose: () => void;
  onDone: () => void;
  oidcAvailable: boolean;
}) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      onClick={(e) => e.target === e.currentTarget && ref.current?.close()}
      className="m-auto w-[min(24rem,calc(100vw-2rem))] rounded-lg bg-spruce-900 p-0 text-bone-300 backdrop:bg-spruce-950/70 backdrop:backdrop-blur-sm"
    >
      <div className="p-6">
        <h2 className="display text-2xl font-bold uppercase leading-none tracking-tight text-bone-100">
          {reason === 'superseded'
            ? 'That sign-in has ended'
            : reason === 'stale-link'
              ? 'That link is out of date'
              : 'Sign in to do that'}
        </h2>
        <p className="mt-2 text-sm leading-snug text-bone-500">
          {reason === 'superseded'
            ? 'The household code was changed, so everyone signed in with the old one has been signed out. Whoever changed it has the new one.'
            : reason === 'stale-link'
              ? 'The code in that link no longer works — it was probably sent before the household code changed. Ask for the new one, or sign in below.'
              : 'Browsing is open to everyone. Voting, logging a day and suggesting somewhere new are the household\u2019s, so they need a sign-in.'}
        </p>
        <SignInForm onDone={onDone} oidcAvailable={oidcAvailable} className="mt-5" />
        <button
          type="button"
          onClick={() => ref.current?.close()}
          className="mt-3 w-full rounded-md px-4 py-2 text-sm text-bone-500 transition-colors hover:text-bone-100"
        >
          {reason === 'superseded' || reason === 'stale-link' ? 'Not now' : 'Keep looking instead'}
        </button>
      </div>
    </dialog>
  );
}

function Splash({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-dvh items-center justify-center px-5 text-bone-500">{children}</div>;
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.hint ? `${error.message} ${error.hint}` : error.message;
  return error instanceof Error ? error.message : String(error);
}

/**
 * Render a saved plan through the same card as a generated one. Opening a
 * featured day gives you a working copy — reroll the lunch stop, save it as
 * your own — rather than a read-only page.
 */
function planToBuilt(plan: Plan, budget: number): BuiltPlan {
  const hero = plan.stops.find((s) => s.slot === 'hero') ?? plan.stops[0];
  return {
    heroId: hero?.outing.id ?? '',
    title: plan.title,
    stops: plan.stops.map((s) => ({ ...s, pairedWithHero: false, pairNote: null })),
    totals: { ...plan.totals, overBudget: plan.totals.totalCost > budget, budget },
    seed: plan.generatorSeed ?? '',
    constraints: {
      season: plan.seasonHint[0] ?? 'any',
      maxDrive: Math.max(90, plan.totals.maxDrive),
      // A saved plan is a fact, not a proposal — reflect the day it actually
      // is rather than clamping it to the default ceiling and implying the
      // stored plan breaks a rule.
      maxHours: Math.max(DEFAULT_MAX_HOURS, plan.totals.totalHours),
      budget,
      excludeIds: [],
      pinnedIds: [],
      slots: ['food', 'addon'],
    },
    notes: plan.note ? [plan.note] : [],
  };
}
